import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import useForm from 'assets/components/form/hooks/Form';
import Icon from 'assets/components/icon/Icon';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import FileInput from 'assets/components/inputs/file/FileInput';
import ThemeFileInputDropzone from 'assets/components/inputs/file/themes/Theme.FileInput.Dropzone';
import { inputStyles } from 'assets/components/inputs/Input';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import ThemeWrapperPaper from 'assets/themes/wrappers/Theme.Wrapper.Paper';
import { switchPlacesInArray } from 'assets/utils/data/Data';
import { isEmptyValue } from 'assets/utils/data/Object';
import { combineStrings } from 'assets/utils/data/String';
import { createPathFromRoute } from 'assets/utils/dom/UrlParsing';
import { sortBy } from 'lodash';
import EngineType from 'models/enums/EngineType';
import InputType from 'models/enums/InputType';
import ProductRefundDocumentPropertyPath from 'models/enums/ProductRefundDocumentPropertyPath';
import { DefaultValueInput } from 'module/productManagement/components/productDocuments/DefaultValueInput';
import { productRefundDocumentIndexRoute } from 'module/productManagement/views/productRefundDocument/ProductRefundDocument.Index';
import { Fragment, useEffect, useState } from 'react';
import ProductDocumentPreview from '../../../../../components/productDocuments/ProductDocumentPreview';
import { useProductProvider } from '../../../Product.Hooks';

export default function ProductEditRefundDocuments() {
  const { lang, translate } = getLocales();
  const {
    form,
    uploadedProductRefundDocuments,
    newProductRefundDocuments,
    product,
    uploadProductRefundDocumentFile,
    deleteProductRefundDocumentFile,
  } = useProductProvider();

  const [documentCategory, setDocumentCategory] = useState<Model.Enum.EngineType>(EngineType.INTERNAL_COMBUSTION);
  const docForm = useForm<Utils.FormData<Model.IProductRefundDocument & { isFileReplaced?: boolean }>>({
    default: {},
    onSubmit: (document) => {
      const index = form.data.productRefundDocuments?.findIndex((p) => p.id === document.id);
      form.data.productRefundDocuments.splice(index, 1, document);
      form.update({
        productRefundDocuments: [...form.data.productRefundDocuments],
      });
      docForm.reload();
    },
  });

  useEffect(() => {
    if (docForm.data.fillablePdfFields) return;
    const doc = product?.productRefundDocuments?.find((d) => d.id === docForm.data.id);
    if (doc) docForm.set(doc);
  }, [product, docForm.data.id]);

  function updateFillablePdfField(fillablePdfField: Utils.FormData<Model.IProductRefundFillablePdfField>) {
    const index = docForm.data.fillablePdfFields?.findIndex((p) => p.id === fillablePdfField.id);
    docForm.data.fillablePdfFields.splice(index, 1, fillablePdfField);
    docForm.update({
      fillablePdfFields: [...docForm.data.fillablePdfFields],
    });
  }

  const fillablePdfFields = docForm.data.fillablePdfFields?.filter((f) => f.key);

  const allFiles = sortBy([...uploadedProductRefundDocuments, ...newProductRefundDocuments], (it) => it.orderNumber);
  function reorderfile(index: number, delta: -1 | 1) {
    form.update({
      productRefundDocuments: switchPlacesInArray(allFiles, index, delta).map((it, i) => ({
        ...it,
        orderNumber: i + 1,
      })),
    });
  }
  return (
    <>
      <ProductDocumentPreview
        documentPageName={lang.productRefundDocuments}
        children={
          <>
            <div
              style={{
                fontSize: Theme.FontSize.L,
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              {lang.refundDocuments}
            </div>
            <div
              style={{
                padding: Theme.Size.M,
                display: docForm.data.id ? 'none' : undefined,
              }}
            >
              <CheckInput
                onChange={({ isForElectricVehicle }) => {
                  setDocumentCategory(isForElectricVehicle ? EngineType.ELECTRIC : EngineType.INTERNAL_COMBUSTION);
                }}
                name="isForElectricVehicle"
                label={lang.isForElectricVehicle}
                class={inputStyles.check.switch}
                value={documentCategory === EngineType.ELECTRIC}
              />
              <br />
              <FileInput
                name="file"
                fileLimit={1}
                onChange={async ({ file }) => {
                  const formFile = file && file[0]?.original;
                  uploadProductRefundDocumentFile({ formFile, documentCategory, orderNumber: allFiles.length + 1 });
                }}
                class={ThemeFileInputDropzone}
                error={docForm.errors.formFile?._objectError}
                disabled={!form.data?.id}
              />
            </div>
          </>
        }
        documents={allFiles.map((it, i) => ({
          id: it.id,
          orderNumber: it.orderNumber,
          url: it.mediaFile.url,
          infoUrl: createPathFromRoute(productRefundDocumentIndexRoute, { id: it?.id }),
          name: it.mediaFile?.name || lang.unknownDocument,
          info: !it.id ? lang.new : lang.uploaded,
          description: it.documentCategory,
          isEditing: docForm.data.id === it.id,
          isDeletable: true,
          onClick: !it.id ? undefined : () => (docForm.data.id === it.id ? docForm.reload() : docForm.set(it)),
          onDelete: !it.id ? undefined : () => deleteProductRefundDocumentFile(it.id),
          onReorder: docForm.data.id ? undefined : (delta) => reorderfile(i, delta),
        }))}
      />
      <br />

      {!isEmptyValue(docForm.data) && (
        <FormWrapper
          class={ThemeWrapperPaper}
          children={
            <>
              <h2>{lang.replaceFile}</h2>
              <FileInput
                name="file"
                fileLimit={1}
                onChange={async ({ file }) => {
                  const formFile = file && file[0]?.original;
                  docForm.update({ formFile, isFileReplaced: true });
                }}
                class={ThemeFileInputDropzone}
                error={docForm.errors.formFile?._objectError}
                disabled={!form.data?.id}
                value={
                  docForm.data.formFile
                    ? [
                        {
                          original: docForm.data.formFile,
                          type: docForm.data.formFile?.type as Utils.File.MimeType,
                          dataUrl: window.URL.createObjectURL(docForm.data.formFile),
                        },
                      ]
                    : undefined
                }
              />
              {fillablePdfFields && (
                <>
                  <h2>{docForm.data.mediaFile?.name}</h2>
                  <DataMap
                    data={fillablePdfFields}
                    render={({ data: fillablePdfField }) => {
                      const fieldType = fillablePdfField.isDisabled ? 'disabled' : 'enabled';
                      return (
                        <Fragment key={fillablePdfField.id}>
                          <SelectInput
                            name="type"
                            data={[
                              { id: 'enabled', title: combineStrings(' | ', fillablePdfField.key, lang.enabled) },
                              { id: 'disabled', title: combineStrings(' | ', fillablePdfField.key, lang.disabled) },
                            ]}
                            value={fieldType}
                            style={{
                              input:
                                fieldType === 'enabled'
                                  ? {
                                      backgroundColor: Theme.Color.success,
                                      color: 'white',
                                    }
                                  : fieldType === 'disabled'
                                    ? {
                                        backgroundColor: Theme.Color.error,
                                        color: 'white',
                                      }
                                    : {
                                        backgroundColor: Theme.Color.primary,
                                        color: 'white',
                                      },
                            }}
                            onChange={({ type }) => {
                              if (type === 'enabled' || !type) {
                                updateFillablePdfField({ ...fillablePdfField, isDisabled: false });
                              } else if (type === 'disabled') {
                                updateFillablePdfField({ ...fillablePdfField, isDisabled: true });
                              } else {
                                updateFillablePdfField({ ...fillablePdfField, isDisabled: true });
                              }
                            }}
                          />
                          {!fillablePdfField.isDisabled && (
                            <>
                              <SelectInput
                                name="propertyPath"
                                label={lang.property}
                                data={Object.values(ProductRefundDocumentPropertyPath).map((val) => ({
                                  id: val,
                                  title: val,
                                }))}
                                value={fillablePdfField.propertyPath}
                                onChange={(updated) =>
                                  updateFillablePdfField({ ...fillablePdfField, ...updated, defaultValue: null })
                                }
                              />
                              <InputWrapper>
                                <SelectInput
                                  name="type"
                                  label={lang.type}
                                  data={Object.values(InputType).map((val) => ({ id: val, title: translate(val) }))}
                                  value={fillablePdfField.type}
                                  onChange={(updated) =>
                                    updateFillablePdfField({ ...fillablePdfField, ...updated, defaultValue: null })
                                  }
                                />
                                <TextInput
                                  name="group"
                                  label={lang.group}
                                  value={fillablePdfField.group}
                                  onChange={(updated) => updateFillablePdfField({ ...fillablePdfField, ...updated })}
                                />
                                <TextInput
                                  name="label"
                                  label={lang.label}
                                  value={fillablePdfField.label}
                                  onChange={(updated) => updateFillablePdfField({ ...fillablePdfField, ...updated })}
                                />
                              </InputWrapper>
                              <DefaultValueInput
                                fillablePdfField={fillablePdfField}
                                onValueChange={(updated) => updateFillablePdfField({ ...fillablePdfField, ...updated })}
                              />
                              <CheckInput
                                name="isRequired"
                                label={lang.isRequired}
                                value={fillablePdfField.isRequired}
                                onChange={(updated) => updateFillablePdfField({ ...fillablePdfField, ...updated })}
                                class={inputStyles.check.switch}
                              />
                            </>
                          )}
                        </Fragment>
                      );
                    }}
                  />
                </>
              )}
              <Button
                render={lang.save}
                media={<Icon class="fas-save" />}
                onClick={docForm.submit}
                disabled={docForm.hasErrors}
                active={!docForm.hasErrors}
                class={ThemeButtonCircle}
                htmlElementProps={{ title: lang.save }}
              />
            </>
          }
        />
      )}
    </>
  );
}
